import type {
  AiChatNode,
  AiChatState,
  AiChatTurn,
  AiPart,
  UiMessage,
} from "@intelligems/ig-types";

import { AiChatStateProcessor } from "./ai-chat-state-processor";
import { UiMessagePartTypeGuardProcessor } from "./ui-message-part-type-guard-processor";

export function getLastMessagePart(
  messages: UiMessage[],
  messageIndex: number
): AiPart | undefined {
  const parts = messages[messageIndex].parts;

  return parts[parts.length - 1];
}

export function getLastMessagePartFromMessageId(
  messages: UiMessage[],
  messageId: string
): AiPart | undefined {
  const messageIndex = messages.findIndex((msg) => msg.id === messageId);
  if (messageIndex === -1) return undefined;

  return getLastMessagePart(messages, messageIndex);
}

export function getPartByIdAndMessageId(
  messages: UiMessage[],
  messageId: string,
  partId: string
): AiPart | undefined {
  const messageIndex = messages.findIndex((msg) => msg.id === messageId);
  if (messageIndex === -1) return undefined;

  return messages[messageIndex].parts.find(
    (part) => "id" in part && part.id === partId
  );
}
export function getPartByToolCallIdAndMessageId(
  messages: UiMessage[],
  messageId: string,
  partId: string
): AiPart | undefined {
  const messageIndex = messages.findIndex((msg) => msg.id === messageId);
  if (messageIndex === -1) return undefined;

  return messages[messageIndex].parts.find(
    (part) => "toolCallId" in part && part.toolCallId === partId
  );
}

export function getNodeMessageById(
  messages: UiMessage[],
  messageId: string
): UiMessage | undefined {
  return messages.find((msg) => msg.id === messageId);
}

export function getTurnById(
  draft: AiChatState,
  _turnId: string
): AiChatTurn | undefined {
  const turnId = _turnId || "ai-analytics-turn";

  const turnLookup = draft.turnLookups[turnId];
  if (turnLookup !== undefined) {
    return draft.turns[turnLookup];
  }

  return draft.turns.find((turn) => turn.turnId === turnId);
}

export function getNodeById(
  draft: AiChatState,
  message: AiChatTurn,
  nodeId: string
): AiChatNode | undefined {
  const nodeLookup = draft.nodeLookups[nodeId];
  if (nodeLookup && nodeLookup.nodeIndex !== undefined) {
    return message.nodes[nodeLookup.nodeIndex];
  }

  return message.nodes.find((node) => node.id === nodeId);
}

export function getOrCreateTurn(
  draft: AiChatState,
  payload: { turnId: string }
) {
  let turn = getTurnById(draft, payload.turnId);

  if (!turn) {
    turn = AiChatStateProcessor.createEmptyTurn(draft, payload.turnId);
  }
  return turn;
}

export function getOrCreateTurnAndNode(
  draft: AiChatState,
  payload: { turnId: string; nodeId: string; nodeName: string }
) {
  const turn = getOrCreateTurn(draft, payload);

  let node = getNodeById(draft, turn, payload.nodeId);

  if (!node) {
    node = AiChatStateProcessor.createNode(draft, turn, {
      id: payload.nodeId,
      name: payload.nodeName,
      status: "pending",
    });
  }

  return {
    turn,
    node,
  };
}

export function getLastNodeByTurnId(draft: AiChatState, turnId: string) {
  const turn = getTurnById(draft, turnId);

  if (!turn) {
    return undefined;
  }

  return turn.nodes[turn.nodes.length - 1];
}

export function getOrCreateNodeMessageById(
  draft: AiChatNode,
  messageId: string,
  role: "system" | "user" | "assistant",
  timestamp: number
): UiMessage {
  let message = getNodeMessageById(draft.messages, messageId);
  if (!message) {
    message = {
      id: messageId,
      role,
      parts: [],
      timestamp,
    };
    draft.messages.push(message);
  }
  return message;
}

export function extractTextContent(parts: AiPart[]) {
  return parts
    .filter(UiMessagePartTypeGuardProcessor.hasText)
    .map((part) => part.text)
    .join("");
}

export function buildTranscript(aiChat: AiChatState): string {
  const lines: string[] = [];
  for (const turn of aiChat.turns) {
    for (const node of turn.nodes) {
      for (const message of node.messages) {
        if (message.role === "system") continue;
        const label = message.role === "user" ? "User" : "Assistant";
        const textParts = (message.parts as AiPart[])
          .filter(UiMessagePartTypeGuardProcessor.hasText)
          .map((part) => part.text.trim())
          .filter(Boolean);
        for (const text of textParts) {
          lines.push(`${label}:\n${text}`);
        }
      }
    }
  }
  return lines.join("\n\n");
}
